// Real application state for the portal. Provides the `useApp()` context shape the screens
// consume (env, apiKey, partner, lang, codeDark, setters), backed by:
//   • a real environment switcher that repoints the /v2 base URL (config/environments.ts)
//   • a real authenticated session (partner api-key + trust flag), held in memory
//   • UI prefs (code theme, sample language) in localStorage
import { useState, useEffect, useMemo, useContext, useRef, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { ENVIRONMENTS, ENVIRONMENT_IDS, defaultEnvironment, apiBaseUrl, type EnvironmentId } from '@/config/environments';
import { config } from '@/config';

import { refreshAccessToken, logout } from '@/api/auth';
import { fetchMe } from '@/api/portal';
import {
  createAuthChannel,
  isUserActive,
  startActivityTracking,
  withRefreshLock,
  type AuthChannel,
  type TokenBroadcast,
} from '@/lib/authCoordination';
import { registerAuthBridge } from '@/lib/authBridge';
import { AppCtx, Select } from '@/components/ds';
import { TEST_KEY, LIVE_KEY } from '@/data';
import type { AppContextValue, Session, PartnerType } from '@/lib/types';

/** Treat the token as expired this long before its real expiry, to cover clock skew and in-flight time. */
const SKEW_MS = 30_000;
/** Proactively refresh this long before expiry, so an active user's token is renewed before it lapses. */
const PROACTIVE_SKEW_MS = 60_000;

// ---- Persistence -------------------------------------------------------------------------
// SECURITY: the partner api-key and session are held in memory only (React state) — never in
// session/localStorage. Only non-secret UI prefs (code theme, sample language) are persisted.
const PREFS_KEY = 'payouts.portal.prefs';

interface Prefs {
  lang: string;
  codeDark: boolean;
}

function readJson<T>(storage: Storage, key: string): T | null {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [env, setEnv] = useState<EnvironmentId>(() => defaultEnvironment(config.environment, window.location.hostname));
  const [auth, setAuth] = useState<Session | null>(null);
  // The portal session lives in an HTTP-only cookie set by the backend after the auth-service
  // callback, so it survives reloads and can only be read by asking the backend for it.
  const [signedIn, setSignedIn] = useState(false);
  // Whether the signed-in user has finished onboarding. `undefined` until `/session` resolves — route
  // guards wait on `authLoading` before reading it.
  const [onboarded, setOnboarded] = useState<boolean | undefined>(undefined);
  // Whether the signed-in partner is cleared to operate (manage keys) in production (from the production
  // `/me` gate call). `undefined` until `/me` resolves; the live keys UI gates on it and the prod backend
  // enforces it.
  const [operational, setOperational] = useState<boolean | undefined>(undefined);
  // Set when the bootstrap `/me` call fails outright (the melio-api DB is always configured, so a
  // failure is a real error, not "undeterminable"). Guards show an error page instead of guessing.
  const [bootError, setBootError] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [prefs, setPrefs] = useState<Prefs>(() => readJson<Prefs>(localStorage, PREFS_KEY) || { lang: 'curl', codeDark: true });

  useEffect(() => {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  }, [prefs]);

  const isLive = !!ENVIRONMENTS[env].live;

  const navigate = useNavigate();

  // The access token (and its expiry) held in memory only. `/auth/token/refresh` is the ONLY endpoint
  // that touches the rotating refresh cookie, so we funnel every mint through it. Two layers of
  // de-duplication keep the rotating one-time refresh token safe: `refreshInFlight` collapses concurrent
  // callers in THIS tab, and `withRefreshLock` serializes refreshes across ALL tabs (see authCoordination).
  const accessTokenRef = useRef<{ token: string; expiresAt?: number } | null>(null);
  const refreshInFlight = useRef<Promise<string | null> | null>(null);
  // The proactive-refresh timer, the cross-tab channel, and the current scheduler (a ref so the refresh
  // path can re-arm the timer without a dependency cycle between doRefresh/getAccessToken/scheduler).
  const refreshTimer = useRef<number | null>(null);
  const channelRef = useRef<AuthChannel | null>(null);
  const scheduleRef = useRef<() => void>(() => {});
  const redirectingRef = useRef(false);

  // Terminal auth failure: drop the in-memory token and, from a protected page, redirect to sign-in with
  // a `returnTo` so the user lands back where they were after re-authenticating (the backend round-trips
  // `returnTo` through the httpOnly OAuth transaction cookie — the auth provider never carries it). On a
  // public page we just clear state and let the route guards handle it, rather than yanking the user off.
  const goSignIn = useCallback(() => {
    if (redirectingRef.current) return;
    const { pathname, search, hash } = window.location;
    const onProtectedPage = pathname.startsWith('/dashboard') || pathname === '/onboarding';
    if (!onProtectedPage) return;
    redirectingRef.current = true;
    navigate(`/signin?returnTo=${encodeURIComponent(`${pathname}${search}${hash}`)}`, { replace: true });
  }, [navigate]);

  const expireSession = useCallback(
    (broadcast = true) => {
      accessTokenRef.current = null;
      if (refreshTimer.current !== null) {
        clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
      if (broadcast) channelRef.current?.postSignOut();
      setSignedIn(false);
      goSignIn();
    },
    [goSignIn]
  );

  const doRefresh = useCallback(async (force = false): Promise<string | null> => {
    return withRefreshLock(async () => {
      // Another tab may have refreshed (and broadcast the token) while we waited for the lock; unless the
      // caller forces (a 401 rejected the token we hold), reuse a still-valid token instead of rotating again.
      if (!force) {
        const cur = accessTokenRef.current;
        if (cur && (cur.expiresAt === undefined || cur.expiresAt - Date.now() > SKEW_MS)) return cur.token;
      }
      try {
        const { accessToken, expiresAt, email } = await refreshAccessToken();
        const exp = expiresAt ? Date.parse(expiresAt) : undefined;
        accessTokenRef.current = { token: accessToken, expiresAt: exp };
        setSignedIn(true);
        setAuth((a) => ({ ...(a || {}), email: email ?? a?.email }));
        channelRef.current?.postToken({ accessToken, expiresAt: exp });
        scheduleRef.current();
        return accessToken;
      } catch {
        accessTokenRef.current = null;
        setSignedIn(false);
        return null;
      }
    });
  }, []);

  const getAccessToken = useCallback(
    (force = false): Promise<string | null> => {
      if (!force) {
        const current = accessTokenRef.current;
        if (current && (current.expiresAt === undefined || current.expiresAt - Date.now() > SKEW_MS)) {
          return Promise.resolve(current.token);
        }
      }
      if (refreshInFlight.current) return refreshInFlight.current;
      const pending = doRefresh(force).finally(() => {
        refreshInFlight.current = null;
      });
      refreshInFlight.current = pending;
      return pending;
    },
    [doRefresh]
  );

  // Proactively refresh shortly before expiry, but only while the user is active (visible tab + recent
  // interaction) — an idle session is left to lapse. Re-armed after every token change.
  const scheduleProactiveRefresh = useCallback(() => {
    if (refreshTimer.current !== null) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
    const current = accessTokenRef.current;
    if (!current?.expiresAt) return; // no known expiry → rely on lazy + 401-driven refresh
    const delay = Math.max(current.expiresAt - Date.now() - PROACTIVE_SKEW_MS, 0);
    refreshTimer.current = window.setTimeout(() => {
      refreshTimer.current = null;
      if (!isUserActive()) return; // idle → lapse; the visibility handler re-arms when the user returns
      void getAccessToken(true).then((token) => {
        if (!token) expireSession(true);
      });
    }, delay);
  }, [getAccessToken, expireSession]);
  scheduleRef.current = scheduleProactiveRefresh;

  // A token minted by another tab arrives here: adopt it (no network refresh) and re-arm our own timer.
  const onRemoteToken = useCallback((token: TokenBroadcast) => {
    accessTokenRef.current = { token: token.accessToken, expiresAt: token.expiresAt };
    setSignedIn(true);
    scheduleRef.current();
  }, []);

  // Open the cross-tab channel + start activity tracking once, on mount. Handlers are read through a ref
  // so the channel itself is created exactly once (and never misses messages by being torn down/rebuilt).
  const channelHandlers = useRef({ onToken: onRemoteToken, onSignOut: () => expireSession(false) });
  channelHandlers.current = { onToken: onRemoteToken, onSignOut: () => expireSession(false) };
  useEffect(() => {
    const channel = createAuthChannel({
      onToken: (token) => channelHandlers.current.onToken(token),
      onSignOut: () => channelHandlers.current.onSignOut(),
    });
    channelRef.current = channel;
    const stopActivity = startActivityTracking();
    return () => {
      channel.close();
      channelRef.current = null;
      stopActivity();
      if (refreshTimer.current !== null) clearTimeout(refreshTimer.current);
    };
  }, []);

  // Expose the coordinated refresh + session-expired handling to the plain fetch clients (which power
  // the 401 → refresh → retry-once, then redirect-to-sign-in behaviour).
  useEffect(
    () => registerAuthBridge({ forceRefresh: () => getAccessToken(true), onSessionExpired: () => expireSession(true) }),
    [getAccessToken, expireSession]
  );

  // Background tabs throttle timers, so on return-to-visible re-arm the timer and, if the token is
  // already near expiry and the user is active, refresh right away.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      scheduleRef.current();
      const current = accessTokenRef.current;
      if (current?.expiresAt && current.expiresAt - Date.now() < PROACTIVE_SKEW_MS && isUserActive()) {
        void getAccessToken(true).then((token) => {
          if (!token) expireSession(true);
        });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [getAccessToken, expireSession]);

  // Load exactly once (StrictMode double-invokes effects): mint a token, then ask the portal for the
  // onboarding verdict (no partner / no entity → onboarding). A failed `/me` is a real bootstrap error
  // (the DB is always configured), so we flag it and let the guard show an error page.
  const sessionLoaded = useRef(false);
  useEffect(() => {
    if (sessionLoaded.current) return;
    sessionLoaded.current = true;
    void (async () => {
      const token = await getAccessToken();
      if (!token) {
        setAuthLoading(false);
        return;
      }
      try {
        const me = await fetchMe(token);
        setOnboarded(me.onboarded);
        setOperational(!!me.operational);
        if (me.partnerName || me.companyName)
          setAuth((a) => ({
            ...(a || {}),
            partnerName: me.partnerName ?? a?.partnerName,
            companyName: me.companyName ?? a?.companyName,
          }));
      } catch {
        setBootError(true);
      } finally {
        setAuthLoading(false);
      }
    })();
  }, [getAccessToken]);

  const ctx = useMemo<AppContextValue>(
    () => ({
      // environment (drives the real API base URL)
      env,
      envLabel: ENVIRONMENTS[env].label,
      isLive,
      apiBase: apiBaseUrl(env),
      setEnv,
      // authenticated session
      authed: signedIn || !!(auth?.partnerName || auth?.companyName),
      authLoading,
      bootError,
      onboarded,
      operational,
      hasKey: !!auth?.apiKey,
      apiKey: auth?.apiKey || (isLive ? LIVE_KEY : TEST_KEY),
      partner: (auth?.partner as PartnerType) || 'self-serve',
      companyName: auth?.companyName,
      partnerName: auth?.partnerName,
      email: auth?.email,
      portalToken: auth?.portalToken,
      signIn: (session: Session) => setAuth((a) => ({ ...(a || {}), ...session })),
      signOut: () => {
        // Don't clear signedIn/auth here — that makes the route guard bounce to /signin before the
        // real logout redirect fires (a flash of the sign-in page). logout() does a full-page redirect
        // (auth-app logout → back to /signin), which tears down the SPA and resets state anyway. Tell the
        // other tabs to sign out too, and stop the proactive timer so it can't fire during the redirect.
        accessTokenRef.current = null;
        if (refreshTimer.current !== null) {
          clearTimeout(refreshTimer.current);
          refreshTimer.current = null;
        }
        channelRef.current?.postSignOut();
        void logout();
      },
      markOnboarded: () => setOnboarded(true),
      getAccessToken,
      setApiKey: (apiKey: string) => setAuth((a) => ({ ...(a || {}), apiKey })),
      setPartner: (partner: PartnerType) => setAuth((a) => ({ ...(a || {}), partner })),
      // UI prefs
      lang: prefs.lang,
      codeDark: prefs.codeDark,
      setLang: (lang: string) => setPrefs((p) => ({ ...p, lang })),
      setCodeDark: (codeDark: boolean) => setPrefs((p) => ({ ...p, codeDark })),
    }),
    [env, isLive, auth, signedIn, onboarded, operational, authLoading, bootError, prefs, getAccessToken]
  );

  return <AppCtx.Provider value={ctx}>{children}</AppCtx.Provider>;
}

// ---- Environment switcher (top-bar control) ----------------------------------------------
export function EnvSwitcher({ width = 168 }: { width?: number }) {
  const ctx = useContext(AppCtx);
  if (!ctx) return null;
  const options = ENVIRONMENT_IDS.map((id) => ({ value: id, label: ENVIRONMENTS[id].label }));
  return <Select value={ctx.env} onChange={(v) => ctx.setEnv(v as EnvironmentId)} options={options} style={{ width, height: 38 }} />;
}
